// eslint-disable-next-line no-unused-vars
import jsPDF from 'jspdf'; // Required for jspdf-autotable
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

/**
 * Export data to Excel file
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Column definitions [{key: 'fieldName', header: 'Display Name'}]
 * @param {string} filename - Output filename (without extension)
 * @param {string} sheetName - Excel sheet name
 */
export const exportToExcel = (data, columns, filename = 'report', sheetName = 'Sheet1') => {
  try {
    // Transform data to use column headers
    const exportData = data.map(row => {
      const newRow = {};
      columns.forEach(col => {
        let value = row[col.key];
        // Format numbers
        if (typeof value === 'number') {
          value = value.toLocaleString('en-US');
        }
        // Format dates
        if (col.type === 'date' && value) {
          value = new Date(value).toLocaleDateString('en-GB');
        }
        newRow[col.header] = value ?? '';
      });
      return newRow;
    });

    // Create workbook and worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    
    // Set RTL for Hebrew support
    worksheet['!cols'] = columns.map(() => ({ wch: 15 }));
    
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    
    // Generate buffer and save
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${filename}.xlsx`);
    
    return true;
  } catch (error) {
    console.error('Export to Excel failed:', error);
    return false;
  }
};

/**
 * Export data to PDF file using HTML table approach for Hebrew support
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Column definitions [{key: 'fieldName', header: 'Display Name'}]
 * @param {string} filename - Output filename (without extension)
 * @param {string} title - PDF title
 * @param {Object} options - Additional options
 */
export const exportToPDF = (data, columns, filename = 'report', title = '', options = {}) => {
  try {
    // Create a hidden div with HTML table
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.direction = 'rtl';
    container.style.fontFamily = 'Arial, sans-serif';
    
    // Build HTML table
    let html = `
      <style>
        table { border-collapse: collapse; width: 100%; direction: rtl; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: center; font-size: 10px; }
        th { background-color: #4285f4; color: white; }
        tr:nth-child(even) { background-color: #f5f5f5; }
        h2 { text-align: center; color: #333; }
        .footer { text-align: center; font-size: 8px; margin-top: 10px; }
      </style>
    `;
    
    if (title) {
      html += `<h2>${title}</h2>`;
    }
    
    html += '<table><thead><tr>';
    
    // Headers (reversed for RTL)
    const reversedColumns = [...columns].reverse();
    reversedColumns.forEach(col => {
      html += `<th>${col.header}</th>`;
    });
    html += '</tr></thead><tbody>';
    
    // Data rows
    data.forEach(row => {
      html += '<tr>';
      reversedColumns.forEach(col => {
        let value = row[col.key];
        if (typeof value === 'number') {
          value = value.toLocaleString('en-US');
        }
        if (col.type === 'date' && value) {
          value = new Date(value).toLocaleDateString('en-GB');
        }
        html += `<td>${value ?? ''}</td>`;
      });
      html += '</tr>';
    });
    
    html += '</tbody></table>';
    html += `<div class="footer">${new Date().toLocaleDateString('en-GB')}</div>`;
    
    container.innerHTML = html;
    document.body.appendChild(container);
    
    // Use window.print() approach with a new window for better Hebrew support
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl" lang="he">
      <head>
        <meta charset="UTF-8">
        <title>${title || filename}</title>
        <style>
          @media print {
            @page { size: landscape; margin: 10mm; }
          }
          body { font-family: Arial, sans-serif; direction: rtl; padding: 20px; }
          table { border-collapse: collapse; width: 100%; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: center; font-size: 11px; }
          th { background-color: #17a2b8; color: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          tr:nth-child(even) { background-color: #f8f9fa; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          h2 { text-align: center; color: #333; margin-bottom: 20px; }
          .footer { text-align: center; font-size: 10px; margin-top: 20px; color: #666; }
          .total-row { font-weight: bold; background-color: #e9ecef !important; }
        </style>
      </head>
      <body>
        ${title ? `<h2>${title}</h2>` : ''}
        <table>
          <thead><tr>
            ${reversedColumns.map(col => `<th>${col.header}</th>`).join('')}
          </tr></thead>
          <tbody>
            ${data.map(row => `
              <tr>
                ${reversedColumns.map(col => {
                  let value = row[col.key];
                  if (typeof value === 'number') value = value.toLocaleString('en-US');
                  if (col.type === 'date' && value) value = new Date(value).toLocaleDateString('en-GB');
                  return `<td>${value ?? ''}</td>`;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="footer">תאריך הפקה: ${new Date().toLocaleDateString('he-IL')}</div>
      </body>
      </html>
    `);
    printWindow.document.close();
    
    // Auto-trigger print dialog
    printWindow.onload = function() {
      printWindow.print();
    };
    // Fallback for browsers that don't fire onload
    setTimeout(() => {
      printWindow.print();
    }, 500);
    
    // Clean up
    document.body.removeChild(container);
    
    return true;
  } catch (error) {
    console.error('Export to PDF failed:', error);
    return false;
  }
};

/**
 * Terminal Report columns configuration
 */
export const terminalReportColumns = [
  { key: 'date', header: 'תאריך', type: 'date' },
  { key: 'branchName', header: 'סניף' },
  { key: 'accountNumber', header: 'חשבון בנק' },
  { key: 'reference', header: 'אסמכתא' },
  { key: 'batch', header: 'אצווה' },
  { key: 'loadingAmount', header: 'סכום הטענה' },
  { key: 'returnFromTerminal', header: 'סכום הפקדה' }
];

/**
 * Agent Expense Report columns configuration
 */
export const agentExpenseReportColumns = [
  { key: 'date', header: 'תאריך', type: 'date' },
  { key: 'reference', header: 'אסמכתא' },
  { key: 'amount', header: 'סכום' },
  { key: 'description', header: 'תיאור' }
];

const exportUtils = {
  exportToExcel,
  exportToPDF,
  terminalReportColumns,
  agentExpenseReportColumns
};

export default exportUtils;
